import { type FilterState } from 'framework/ViewTypes/components';
import { FilterCondition } from 'framework/types';

const OFFICE_FILTER_SOURCE = 'specification:positionSlotByOfficeSpecification';
const DIRECT_FILTER_SOURCES = [
  'specializationId',
  'technologyId',
  'competencyLevel',
  'businessFunctionId',
  'lifecycle',
  'workerType',
] as const;

type PositionSlotFilterValue = string | number | null | Array<string | number>;
type PositionSlotFilterCondition = Partial<Record<FilterCondition, PositionSlotFilterValue>>;

export type PositionSlotsApiFilter = Record<string, PositionSlotFilterCondition>;

const getFilterValues = (filters: FilterState, source: string): string[] => {
  const value = filters[source]?.[FilterCondition.IN];

  if (Array.isArray(value)) return value;
  return value ? [value] : [];
};

const copySupportedFilters = (filters: FilterState): PositionSlotsApiFilter =>
  DIRECT_FILTER_SOURCES.reduce<PositionSlotsApiFilter>((result, source) => {
    const conditions = filters[source];
    if (!conditions) return result;

    const mappedConditions = Object.entries(conditions).reduce<PositionSlotFilterCondition>(
      (mapped, [condition, value]) => {
        if (value === null || value === '' || (Array.isArray(value) && value.length === 0)) return mapped;

        return { ...mapped, [condition]: value };
      },
      {},
    );

    if (Object.keys(mappedConditions).length) result[source] = mappedConditions;
    return result;
  }, {});

export const buildPositionSlotsFilter = (filters: FilterState, searchValue: string): PositionSlotsApiFilter => {
  const filter = copySupportedFilters(filters);
  const officeIds = getFilterValues(filters, 'officeIds').map(Number).filter(Number.isFinite);

  if (officeIds.length) filter[OFFICE_FILTER_SOURCE] = { in: officeIds };

  if (searchValue.trim()) filter.specialization = { ct: searchValue.trim() };

  return filter;
};
